import { List } from "immutable";
import { IDoorTotalDelta, IDoorTotals } from "reducers/collection";
import lodashGet from "lodash/get";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import lodashOmit from "lodash/omit";

const compareSkus = (base, downValue: string, upValue: string): { [key: string]: boolean } => {
    let skus = lodashCloneDeep(base);
    if (downValue && !upValue) {
        skus = lodashOmit(skus, [downValue]);
    } else if (!downValue && upValue) {
        skus = lodashSet(skus, [upValue], true);
    } else if (downValue && upValue && downValue === upValue) {
        skus = lodashSet(skus, [upValue], true);
    }
    return skus;
};

export const mergeDoorUnitsDeltas = (baseTotals: IDoorTotals, deltas: List<IDoorTotalDelta>) =>
    deltas.reduce((acc, unprocessedDoorTotal): any => {
        const totalConversionRate = unprocessedDoorTotal.totalConversionRate;
        return {
            ...acc,
            [unprocessedDoorTotal.door]: {
                classifications: acc[unprocessedDoorTotal.door] ? acc[unprocessedDoorTotal.door].classifications : {},
                // update count per deliveries date (get dates from downstream/upstream operations)
                deliveries: Object.keys({
                    ...unprocessedDoorTotal.downTotal.deliveries,
                    ...unprocessedDoorTotal.upTotal.deliveries,
                }).reduce(
                    (accd, date) => ({
                        ...accd,
                        [date]: {
                            // null check since up or down stream can be undefined
                            units:
                                (acc[unprocessedDoorTotal.door] && acc[unprocessedDoorTotal.door].deliveries[date]
                                    ? acc[unprocessedDoorTotal.door].deliveries[date].units
                                    : 0) -
                                (unprocessedDoorTotal.downTotal.deliveries[date]
                                    ? unprocessedDoorTotal.downTotal.deliveries[date].units
                                    : 0) +
                                (unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].units
                                    : 0),
                            retail:
                                (acc[unprocessedDoorTotal.door] && acc[unprocessedDoorTotal.door].deliveries[date]
                                    ? acc[unprocessedDoorTotal.door].deliveries[date].retail
                                    : 0) -
                                (unprocessedDoorTotal.downTotal.deliveries[date]
                                    ? unprocessedDoorTotal.downTotal.deliveries[date].retail
                                    : 0) +
                                (unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].retail
                                    : 0),
                            wholesale:
                                (acc[unprocessedDoorTotal.door] && acc[unprocessedDoorTotal.door].deliveries[date]
                                    ? acc[unprocessedDoorTotal.door].deliveries[date].wholesale
                                    : 0) -
                                (unprocessedDoorTotal.downTotal.deliveries[date]
                                    ? unprocessedDoorTotal.downTotal.deliveries[date].wholesale
                                    : 0) +
                                (unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].wholesale
                                    : 0),
                            skus: compareSkus(
                                lodashGet(acc, `${unprocessedDoorTotal.door}.deliveries.${date}.skus`, {}),
                                lodashGet(unprocessedDoorTotal, `downTotal.deliveries.${date}.sku`),
                                lodashGet(unprocessedDoorTotal, `upTotal.deliveries.${date}.sku`)
                            ),
                        },
                    }),
                    {}
                ),
                units:
                    (acc[unprocessedDoorTotal.door] ? acc[unprocessedDoorTotal.door].units : 0) -
                    unprocessedDoorTotal.downTotal.units +
                    unprocessedDoorTotal.upTotal.units,
                retail:
                    (acc[unprocessedDoorTotal.door] ? acc[unprocessedDoorTotal.door].retail : 0) -
                    unprocessedDoorTotal.downTotal.retail +
                    unprocessedDoorTotal.upTotal.retail,
                wholesale:
                    (acc[unprocessedDoorTotal.door] ? acc[unprocessedDoorTotal.door].wholesale : 0) -
                    unprocessedDoorTotal.downTotal.wholesale +
                    unprocessedDoorTotal.upTotal.wholesale,
                skus: compareSkus(
                    lodashGet(acc, `${unprocessedDoorTotal.door}.skus`, {}),
                    unprocessedDoorTotal.downTotal.sku,
                    unprocessedDoorTotal.upTotal.sku
                ),
            },
            _total: {
                // update count per deliveries date (get dates from downstream/upstream operations)
                deliveries: Object.keys({
                    ...unprocessedDoorTotal.downTotal.deliveries,
                    ...unprocessedDoorTotal.upTotal.deliveries,
                }).reduce(
                    (accd, date) => ({
                        ...accd,
                        [date]: {
                            // null check since up or down stream can be undefined
                            units:
                                (acc._total.deliveries[date] ? acc._total.deliveries[date].units : 0) -
                                (unprocessedDoorTotal.downTotal.deliveries[date]
                                    ? unprocessedDoorTotal.downTotal.deliveries[date].units
                                    : 0) +
                                (unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].units
                                    : 0),
                            retail:
                                (acc._total.deliveries[date] ? acc._total.deliveries[date].retail : 0) +
                                ((unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].retail
                                    : 0) -
                                    (unprocessedDoorTotal.downTotal.deliveries[date]
                                        ? unprocessedDoorTotal.downTotal.deliveries[date].retail
                                        : 0)) /
                                    totalConversionRate,
                            wholesale:
                                (acc._total.deliveries[date] ? acc._total.deliveries[date].wholesale : 0) +
                                ((unprocessedDoorTotal.upTotal.deliveries[date]
                                    ? unprocessedDoorTotal.upTotal.deliveries[date].wholesale
                                    : 0) -
                                    (unprocessedDoorTotal.downTotal.deliveries[date]
                                        ? unprocessedDoorTotal.downTotal.deliveries[date].wholesale
                                        : 0)) /
                                    totalConversionRate,
                            skus: compareSkus(
                                lodashGet(acc, `_total.deliveries.${date}.skus`, {}),
                                lodashGet(unprocessedDoorTotal, `downTotal.deliveries.${date}.sku`),
                                lodashGet(unprocessedDoorTotal, `upTotal.deliveries.${date}.sku`)
                            ),
                        },
                    }),
                    acc._total.deliveries
                ), // carry over existing calulations in between door processing
                units: acc._total.units - unprocessedDoorTotal.downTotal.units + unprocessedDoorTotal.upTotal.units,
                retail:
                    acc._total.retail +
                    (unprocessedDoorTotal.upTotal.retail - unprocessedDoorTotal.downTotal.retail) / totalConversionRate,
                wholesale:
                    acc._total.wholesale +
                    (unprocessedDoorTotal.upTotal.wholesale - unprocessedDoorTotal.downTotal.wholesale) /
                        totalConversionRate,
                skus: compareSkus(
                    acc._total.skus,
                    unprocessedDoorTotal.downTotal.sku,
                    unprocessedDoorTotal.upTotal.sku
                ),
            },
        };
    }, baseTotals);
